/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-data.service.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { Dispatch } from 'react';
import { ApolloClient, ObservableQuery } from '@apollo/client';
import { FieldDefsAndRDLsDto } from '@interfaces-clean/field-rdl-definition.interface';
import { GET_FIELDS_AND_DATA_SOURCES } from 'queries/central-monitoring';
import Settings from 'settings/settings';
import { ILocalState, ILocalStateAction } from '@contexts/local-state-context';
import { IUser, IUserContext } from '@contexts/user-context';
import Constants from '@utils/constants';
import { getFieldsAndDataSources } from '@queries/generated/central-monitoring.generated';
// eslint-disable-next-line max-len
import { StudyVisitScheduleFields_StudySubjectVisitScheduleTemplate_visits_StudySubjectVisit as StudyVisitScheduleFields_StudySubjectVisit } from '@queries/generated/fragments.generated';
import { getRestrictedStudy_OpraQueries_study_Study } from '@queries/generated/studies.generated';
import { ProcessedStudyDataResponse, ScoringStudyDataResponse } from '@apis/bff-api/studies';
import { getData } from '@apis/rest-client';

const loadDataSources = async (client: ApolloClient, dispatch: Dispatch<ILocalStateAction>, studyId: string) => {
    // Always need to request both even for a legacy study because you can configure CM2 things that need
    // the CM2 field defs/rdls in a legacy study before it is upgraded.
    Promise.all([
        // Fetch legacy CM field defs and RDLs
        client
            .query({
                query: GET_FIELDS_AND_DATA_SOURCES,
                fetchPolicy: 'network-only',
                variables: { studyId },
            })
            .then((reply: ObservableQuery.Result<getFieldsAndDataSources>) => {
                dispatch({ type: 'SET_FIELDS', fields: reply.data?.fields });
                dispatch({ type: 'SET_DATA_SOURCES', dataSources: reply.data?.rowDataSources });
            })
            .catch((err: any) => {
                if (err.message !== 'Unexpected end of JSON input')
                    console.error('Error loading processed data: ', err);
                else console.warn('No processed data was found for this study.');
            }),
        // Fetch CM2 service field defs and RDLs
        getData<FieldDefsAndRDLsDto>(`CentralMonitoring/FieldsAndRDLs/${studyId}`)
            .then((data: FieldDefsAndRDLsDto) => {
                dispatch({ type: 'SET_CM2_FIELDS_RDLS', cm2Fields: data?.fields, cm2DataSources: data?.rdLs });
            })
            .catch((err: any) => {
                console.error('Error loading CM2 fields defs and RDLs: ', err);
                // Clear any existing values because we don't want the wrong studies values in the UI
                // because we couldn't fetch new ones.
                dispatch({ type: 'SET_CM2_FIELDS_RDLS', cm2Fields: null, cm2DataSources: null });
            }),
    ]).catch((err) => {
        console.error('Error loading field defs and rdls: ', err);
    });
};

const loadDataset = (
    dispatch: Dispatch<ILocalStateAction>,
    user: IUser | IUserContext | null,
    study: getRestrictedStudy_OpraQueries_study_Study,
    datasetId: string = '',
    localState: ILocalState | null = null,
) => {
    if (!user?.token) {
        console.log('Error loading dataset: user not signed in.');
        return;
    }

    if (
        localState?.studyData &&
        ((datasetId !== '' && localState.studyData.dataSetId === datasetId) ||
            (datasetId === '' && localState.studyData.study.Fields['Study.id'] === study.id))
    ) {
        console.log(`Dataset '${datasetId}' already loaded for study: ${study.id}`);
        return Promise.resolve();
    }

    return Promise.all([
        fetch(
            datasetId
                ? `${Settings.api.url()}/api/v1/studies/id/${study.id}/data/${datasetId}`
                : `${Settings.api.url()}/api/v1/studies/id/${study.id}/data`,
            {
                // cache: datasetId ? 'force-cache' : undefined,
                headers: {
                    APIKey: study.organisation?.apiKey ?? '',
                    Authorization: `Bearer ${user.token}`,
                },
            },
        )
            .then((result) => result.json())
            .then((result: ProcessedStudyDataResponse) => dispatch({ type: 'SET_PROCESSED_DATA', data: result }))
            .catch((err) => {
                if (err.message !== 'Unexpected end of JSON input')
                    console.error('Error loading processed data: ', err);
                else console.warn('No processed data was found for this study.');
            }),
        fetch(
            datasetId
                ? `${Settings.api.url()}/api/v1/studies/id/${study.id}/kri/${datasetId}`
                : `${Settings.api.url()}/api/v1/studies/id/${study.id}/kri`,
            {
                // cache: datasetId ? 'force-cache' : undefined,
                headers: {
                    APIKey: study.organisation?.apiKey ?? '',
                    Authorization: `Bearer ${user.token}`,
                },
            },
        )
            .then((result) => result.json())
            .then((result: ScoringStudyDataResponse) => dispatch({ type: 'SET_SCORING_DATA', data: result }))
            .catch((err) => {
                if (err.message !== 'Unexpected end of JSON input') console.error('Error loading scoring data: ', err);
                else console.warn('No scoring data was found for this study.');
            }),
    ]).catch((err) => {
        console.error('Error loading data: ', err);
    });
};

const getLeadVisitField = (
    visits: StudyVisitScheduleFields_StudySubjectVisit[] | null | undefined,
    value: string,
    fieldToFind: 'id' | 'visitId',
    fieldToPut: 'id' | 'visitId',
) => {
    if (visits) {
        const foundVisit: StudyVisitScheduleFields_StudySubjectVisit | null | undefined = visits.find(
            (visit: StudyVisitScheduleFields_StudySubjectVisit | null) => visit && visit[fieldToPut] === value,
        );

        if (foundVisit) return foundVisit[fieldToFind];
        if (!foundVisit) return Constants.ObjectId.Empty;
    }
};

export { loadDataset, loadDataSources, getLeadVisitField };
